import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Slider,
  Rating
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as TaskIcon,
  AccessTime as TimeIcon,
  TrendingUp as GrowthIcon,
  PriorityHigh as UrgentIcon,
  Star as ImportantIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiUrl } from '../../config/api';
import axios from 'axios';

interface Task {
  id: number;
  member_id: number;
  team_id: number;
  title: string;
  description: string;
  urgency: number;
  importance: number;
  time_allocation: number;
  growth_value: number;
  skill_alignment: string;
  status: string;
  assigned_to: number | null;
  member_name: string;
  member_role: string;
  user_email: string;
  delegated_to_name?: string;
  created_at: string;
  updated_at: string;
}

interface TaskFormData {
  member_id: number | '';
  title: string;
  description: string;
  urgency: number;
  importance: number;
  time_allocation: number;
  growth_value: number;
  skill_alignment: string;
  status: string;
  assigned_to: number | '';
}

interface TeamMember {
  id: number;
  user_name: string;
  user_email: string;
  role: string;
  skills?: string;
}

const Tasks: React.FC<{ view: string; onTasksChange?: (tasks: Task[]) => void }> = ({ view, onTasksChange }) => {
  const { user, currentTeam } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filterMemberId, setFilterMemberId] = useState<number | 'all'>('all');
  const [formData, setFormData] = useState<TaskFormData>({
    member_id: '',
    title: '',
    description: '',
    urgency: 2,
    importance: 2,
    time_allocation: 10,
    growth_value: 3,
    skill_alignment: '',
    status: 'not-started',
    assigned_to: ''
  });
  const showDetails = view === 'workshop';

  const fetchTasks = React.useCallback(async () => {
    try {
      setLoading(true);
      if (!user || !currentTeam) {
        setError('Please select a team');
        setTasks([]);
        return;
      }

      const response = await axios.get(apiUrl('/api/tasks'));
      setTasks(response.data);
      onTasksChange?.(response.data);
    } catch (err) {
      setError('Failed to load tasks');
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [user, currentTeam, onTasksChange]);

  const fetchMembers = React.useCallback(async () => {
    try {
      if (!currentTeam) return;
      
      const response = await axios.get(apiUrl(`/api/team-members/team/${currentTeam.id}`));
      setMembers(response.data);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  }, [currentTeam]);

  useEffect(() => {
    if (currentTeam) {
      fetchTasks();
      fetchMembers();
    }
  }, [fetchTasks, fetchMembers, currentTeam]);

  const handleAddTask = () => {
    setEditingTask(null);
    
    // For regular users, automatically set their own member_id
    const defaultMemberId = !isAdmin 
      ? members.find(member => member.user_email === user?.email)?.id || ''
      : '';
    
    setFormData({
      member_id: defaultMemberId,
      title: '',
      description: '',
      urgency: 2,
      importance: 2,
      time_allocation: 10,
      growth_value: 3,
      skill_alignment: '',
      status: 'not-started',
      assigned_to: ''
    });
    setDialogOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setFormData({
      member_id: task.member_id,
      title: task.title,
      description: task.description,
      urgency: task.urgency,
      importance: task.importance,
      time_allocation: task.time_allocation,
      growth_value: task.growth_value,
      skill_alignment: task.skill_alignment,
      status: task.status,
      assigned_to: task.assigned_to || ''
    });
    setDialogOpen(true);
  };

  const handleDeleteTask = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await axios.delete(apiUrl(`/api/tasks/${id}`));
        fetchTasks();
      } catch (err) {
        setError('Failed to delete task');
        console.error('Error deleting task:', err);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const submitData = {
        ...formData,
        assigned_to: formData.assigned_to || null
      };
      
      if (editingTask) {
        await axios.put(apiUrl(`/api/tasks/${editingTask.id}`), submitData);
      } else {
        await axios.post(apiUrl('/api/tasks'), submitData);
      }
      setDialogOpen(false);
      fetchTasks();
    } catch (err) {
      setError('Failed to save task');
      console.error('Error saving task:', err);
    }
  };

  const handleFormChange = (field: keyof TaskFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getUrgencyColor = (urgency: number) => {
    if (urgency >= 4) return 'error';
    if (urgency >= 3) return 'warning';
    return 'info';
  };

  const getImportanceColor = (importance: number) => {
    if (importance >= 4) return 'error';
    if (importance >= 3) return 'warning';
    return 'success';
  };

  const getGrowthValueColor = (value: number) => {
    if (value >= 4) return 'success';
    if (value >= 3) return 'info';
    return 'warning';
  };

  if (!currentTeam) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">
          Please select a team to view and manage tasks.
        </Alert>
      </Container>
    );
  }

  const isAdmin = currentTeam.role === 'admin' || currentTeam.role === 'manager';

  // Filter tasks by selected team member (frontend only)
  const filteredTasks = filterMemberId === 'all' 
    ? tasks 
    : tasks.filter(task => task.member_id === filterMemberId);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Task Management
          </Typography>
          {isAdmin ? (
            <Chip 
              label="Admin View - All Team Tasks" 
              color="primary" 
              size="small"
              sx={{ mr: 1 }}
            />
          ) : (
            <Chip 
              label="Personal View - Your Tasks Only" 
              color="default" 
              size="small"
              sx={{ mr: 1 }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Member</InputLabel>
              <Select
                value={filterMemberId}
                onChange={(e) => setFilterMemberId(e.target.value as number | 'all')}
                label="Filter by Member"
              >
                <MenuItem value="all">All Members</MenuItem>
                {members.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.user_name} ({member.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddTask}
          >
            Add Task
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {filteredTasks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TaskIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {tasks.length === 0 
              ? 'No tasks defined yet'
              : filterMemberId !== 'all'
              ? `No tasks found for selected team member`
              : 'No tasks defined yet'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {tasks.length === 0 
              ? 'Start by adding current tasks and rating their urgency, importance, and growth value. This will help identify delegation opportunities and optimize task distribution.'
              : 'Try selecting a different team member or clear the filter.'}
          </Typography>
          {tasks.length === 0 && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddTask}>
              Add First Task
            </Button>
          )}
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {filteredTasks.map((task) => (
            <Box key={task.id} sx={{ flex: '1 1 400px', minWidth: '350px' }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TaskIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      {task.title}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {task.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      icon={<UrgentIcon />}
                      label={`Urgency: ${task.urgency}/4`} 
                      color={getUrgencyColor(task.urgency)}
                      size="small"
                    />
                    <Chip 
                      icon={<ImportantIcon />}
                      label={`Importance: ${task.importance}/4`} 
                      color={getImportanceColor(task.importance)}
                      size="small"
                    />
                    <Chip 
                      icon={<GrowthIcon />}
                      label={`Growth: ${task.growth_value}/5`} 
                      color={getGrowthValueColor(task.growth_value)}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TimeIcon sx={{ mr: 1, fontSize: 18 }} color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {task.time_allocation}% of time allocation
                    </Typography>
                  </Box>
                  
                  {task.skill_alignment && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Skill alignment:
                      </Typography>
                      <Typography variant="body2">
                        {task.skill_alignment}
                      </Typography>
                    </Box>
                  )}

                  {task.delegated_to_name && (
                    <Box sx={{ mb: 1 }}>
                      <Chip 
                        label={`Delegated to: ${task.delegated_to_name}`} 
                        color="info" 
                        size="small"
                      />
                    </Box>
                  )}
                  
                  <Typography variant="caption" color="text.secondary">
                    Owner: {task.member_name} ({task.member_role})
                  </Typography>
                </CardContent>
                
                <CardActions>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEditTask(task)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDeleteTask(task.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTask ? 'Edit Task' : 'Add New Task'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={formData.member_id}
                onChange={(e) => handleFormChange('member_id', e.target.value)}
                label="Assigned To"
                disabled={!isAdmin}
              >
                {isAdmin ? (
                  members.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.user_name} ({member.role})
                    </MenuItem>
                  ))
                ) : (
                  // Regular users can only select themselves
                  members.filter(member => member.user_email === user?.email).map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.user_name} ({member.role}) - You
                    </MenuItem>
                  ))
                )}
              </Select>
              {!isAdmin && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  You can only assign tasks to yourself
                </Typography>
              )}
            </FormControl>

            <TextField
              label="Task Title"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              fullWidth
              required
              placeholder="e.g., Implement user authentication system"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe the task, requirements, and expected outcomes"
            />

            {showDetails && (
              <>
                <Box>
                  <Typography gutterBottom>Urgency Level: {formData.urgency}/4</Typography>
                  <Slider
                    value={formData.urgency}
                    onChange={(_, value) => handleFormChange('urgency', value)}
                    min={1}
                    max={4}
                    step={1}
                    marks={[
                      { value: 1, label: 'Low' },
                      { value: 2, label: 'Medium' },
                      { value: 3, label: 'High' },
                      { value: 4, label: 'Critical' }
                    ]}
                  />
                </Box>

                <Box>
                  <Typography gutterBottom>Importance Level: {formData.importance}/4</Typography>
                  <Slider
                    value={formData.importance}
                    onChange={(_, value) => handleFormChange('importance', value)}
                    min={1}
                    max={4}
                    step={1}
                    marks={[
                      { value: 1, label: 'Low' },
                      { value: 2, label: 'Medium' },
                      { value: 3, label: 'High' },
                      { value: 4, label: 'Critical' }
                    ]}
                  />
                </Box>

                <Box>
                  <Typography gutterBottom>Time Allocation: {formData.time_allocation}%</Typography>
                  <Slider
                    value={formData.time_allocation}
                    onChange={(_, value) => handleFormChange('time_allocation', value)}
                    min={5}
                    max={100}
                    step={5}
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box>
                  <Typography gutterBottom>Personal Growth Value</Typography>
                  <Rating
                    value={formData.growth_value}
                    onChange={(_, value) => handleFormChange('growth_value', value || 1)}
                    max={5}
                    size="large"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    How valuable is this task for your personal/professional development?
                  </Typography>
                </Box>

                <TextField
                  label="Skill Alignment"
                  value={formData.skill_alignment}
                  onChange={(e) => handleFormChange('skill_alignment', e.target.value)}
                  fullWidth
                  placeholder="Skills this task develops or requires"
                  helperText="e.g., React, Leadership, Problem Solving"
                />

                {/* <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleFormChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="not-started">Not Started</MenuItem>
                    <MenuItem value="in-progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="on-hold">On Hold</MenuItem>
                    <MenuItem value="delegated">Delegated</MenuItem>
                  </Select>
                </FormControl>
              

                {formData.status === 'delegated' && (
                  <FormControl fullWidth>
                    <InputLabel>Delegate To</InputLabel>
                    <Select
                      value={formData.assigned_to}
                      onChange={(e) => handleFormChange('assigned_to', e.target.value)}
                      label="Delegate To"
                    >
                      <MenuItem value="">Select team member</MenuItem>
                      {members.filter(m => m.id !== formData.member_id).map((member) => (
                        <MenuItem key={member.id} value={member.id}>
                          {member.user_name} ({member.role})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} */}
            </>
          )}
          </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.member_id || !formData.title}
          >
            {editingTask ? 'Update' : 'Add'} Task
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Tasks;